import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const NotificationTrackingDashboard = ({ notificationLogs, testResults, selectedClients }) => {
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'sent', 'delivered', 'failed'

  const filteredLogs = notificationLogs?.filter(log => {
    if (filter === 'all') return true;
    return log?.notificationStatus === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'sent': return 'text-blue-600 bg-blue-50';
      case 'delivered': return 'text-green-600 bg-green-50';
      case 'read': return 'text-purple-600 bg-purple-50';
      case 'failed': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return 'Clock';
      case 'sent': return 'Send';
      case 'delivered': return 'CheckCircle2';
      case 'read': return 'Eye';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  // Calculate metrics
  const totalNotifications = notificationLogs?.length || 0;
  const deliveredCount = notificationLogs?.filter(n => n?.notificationStatus === 'delivered')?.length || 0;
  const failedCount = notificationLogs?.filter(n => n?.notificationStatus === 'failed')?.length || 0;
  const successRate = totalNotifications > 0 ? ((deliveredCount / totalNotifications) * 100)?.toFixed(1) : 0;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="Bell" size={20} />
            Real-Time Notification Tracking
          </h3>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-xs text-muted-foreground">Live Monitoring</span>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Total Sent</p>
            <p className="text-2xl font-bold text-foreground">{totalNotifications}</p>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Delivered</p>
            <p className="text-2xl font-bold text-success">{deliveredCount}</p>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Failed</p>
            <p className="text-2xl font-bold text-error">{failedCount}</p>
          </div>
          <div className="bg-background rounded-lg p-3 border border-border">
            <p className="text-xs text-muted-foreground mb-1">Success Rate</p>
            <p className="text-2xl font-bold text-primary">{successRate}%</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'sent', 'delivered', 'failed']?.map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
              {status !== 'all' && (
                <span className="ml-1 opacity-70">
                  ({notificationLogs?.filter(n => n?.notificationStatus === status)?.length || 0})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Notification Log Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Recipient
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Delivery Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Priority
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredLogs?.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  <Icon name="Inbox" size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No notifications to display</p>
                  <p className="text-xs mt-1">Trigger a test violation to see notification logs</p>
                </td>
              </tr>
            ) : (
              filteredLogs?.map((log, index) => (
                <tr key={log?.id || index} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm text-foreground">
                    {new Date(log?.createdAt || log?.scheduledFor)?.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    <span className="px-2 py-1 rounded text-xs bg-muted">
                      {log?.notificationType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    <div>
                      <p className="font-medium">{log?.recipientEmail || 'N/A'}</p>
                      <p className="text-xs text-muted-foreground">{log?.recipientRole}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      getStatusColor(log?.notificationStatus)
                    }`}>
                      <Icon name={getStatusIcon(log?.notificationStatus)} size={14} />
                      {log?.notificationStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {log?.deliveredAt ? (
                      <span>
                        {(() => {
                          const sent = new Date(log?.sentAt || log?.scheduledFor);
                          const delivered = new Date(log?.deliveredAt);
                          const latency = Math.round((delivered - sent) / 1000);
                          return `${latency}s`;
                        })()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      log?.priority === 'high' ? 'bg-red-100 text-red-700' :
                      log?.priority === 'medium'? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {log?.priority}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {filteredLogs?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <p className="text-xs text-muted-foreground">
            Showing {filteredLogs?.length} of {notificationLogs?.length} notifications
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationTrackingDashboard;